import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import {
  Router,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
  ActivatedRoute,
  NavigationStart,
} from "@angular/router";
import { NgxSpinnerService } from "ngx-spinner";
import { NgxPermissionsService } from "ngx-permissions";
import { AuthService } from "./shared/services/auth.service";
import { MessageService } from "./shared/services/message.service";
import { ToastrService } from "ngx-toastr";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { LoggingService } from "./logging.service";
import html2canvas from "html2canvas";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ParadeExistingModalDialogComponent } from "./pages/parade/parade-existing-modal/parade-existing-modal";
import { ParadeCreateModalDialogComponent } from "./pages/parade/parade-create-modal/parade-create-modal";

@Component({
  selector: "add-file",
  templateUrl: "./add-file-modal.html",
})
export class AddFileModalComponent implements AfterViewInit {
  filelabel: any;
  fileForm: FormGroup;

  constructor(
    public dialogRef: MatDialogRef<AddFileModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit() {
    this.initializeFileForm();
  }

  initializeFileForm() {
    this.fileForm = new FormGroup({
      label: new FormControl("", [Validators.required]),
    });
  }

  ngAfterViewInit() {
    this.initializeFileForm();
  }

  saveTemplate() {
    this.dialogRef.close(this.fileForm.value.label);
  }

  cancel() {
    this.dialogRef.close(false);
  }
}
@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit {
  @ViewChild("screenshotContainer", { static: false })
  screenshotContainer!: ElementRef<HTMLDivElement>;
  @ViewChild("videoElement") videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild("canvasElement") canvasElement!: ElementRef<HTMLCanvasElement>;
  private selectionBox: HTMLElement;
  currentCaptureStatus = false;
  paradeImgArray: any = [];
  iframeEmbedded = false;
  isVendor = false;
  leftX: number;
  topY: number;
  title = "kairhos-front-end";
  isLoggedIn = false;
  windowScrolled = false;
  stream: MediaStream | null = null;
  screenTrack: MediaStreamTrack | null = null;
  is404 = false;
  isParadePage = false;

  // References for crop modal and esc listener
  private cropModalElement: HTMLElement | null = null;
  private cropModalEscListener: ((ev: KeyboardEvent) => void) | null = null;
  private cropModalResolver: ((value: string | null) => void) | null = null;

  constructor(
    private readonly router: Router,
    private readonly spinner: NgxSpinnerService,
    private readonly renderer: Renderer2,
    private readonly messageService: MessageService,
    private readonly toastrService: ToastrService,
    private readonly matDialog: MatDialog,
    private readonly activatedRoute: ActivatedRoute,
    private readonly loggingService: LoggingService,
    private readonly permissionsService: NgxPermissionsService,
    private readonly authService: AuthService
  ) {
    this.activatedRoute.queryParams.subscribe((params: any) => {
      const embedded =
        params?.embedded ||
        new URL(window.location.href).searchParams.get("embedded");
      if (embedded === "true") {
        this.messageService.setIframeData(true);
        this.iframeEmbedded = true;
      }
    });

    this.router.events.subscribe((event: any) => {
      this.navigationInterceptor(event);
    });
  }

  // Shows and hides the loading spinner during RouterEvent changes
  navigationInterceptor(event: any): void {
    if (event instanceof NavigationStart) {
      //  this.spinner.show()
    }
    if (event instanceof NavigationEnd) {
      this.is404 = this.router.url === "/404";
      this.spinner.hide();
      this.isParadePage = this.router.url.includes("parade/detail/");
    }

    // Set loading state to false in both of the below events to hide the spinner in case a request fails
    if (event instanceof NavigationCancel) {
      this.spinner.hide();
    }
    if (event instanceof NavigationError) {
      this.spinner.hide();
    }
  }
  ngOnInit(): void {
    this.authService.isVendorLogin.subscribe((res: any) => {
      this.isVendor = res;
    });
    this.permissionsService.loadPermissions(
      JSON.parse(localStorage.getItem("permission") || "{}")?.permissions || []
    );
    this.permissionsService.addPermission(
      JSON.parse(localStorage.getItem("permission") || "{}")?.user_group || []
    );
    this.permissionsService.addPermission(
      JSON.parse(localStorage.getItem("permission") || "{}")
        ?.accessible_modules || []
    );
    this.authService.loginStatus.subscribe((res: any) => {
      this.isLoggedIn = res || localStorage.getItem("token");
    });
    this.messageService.getIframeData().subscribe((res: any) => {
      this.iframeEmbedded = typeof res !== "object";
    });

    window.addEventListener("scroll", () => {
      this.windowScrolled = window.scrollY !== 0;
    });

    this.messageService.getCaptureStatus().subscribe((res: any) => {
      if (typeof res === "object") {
        this.currentCaptureStatus = false;
      } else {
        this.currentCaptureStatus = res;
      }
    });
  }

  scrollToTop(): void {
    window.scrollTo(0, 0);
  }

  capture() {
    this.toastrService.info("Parade mode started");
    this.currentCaptureStatus = !this.currentCaptureStatus;
    this.messageService.setCaptureStatus(this.currentCaptureStatus);
  }

  removeCapture() {
    this.currentCaptureStatus = !this.currentCaptureStatus;
    this.messageService.setCaptureStatus(this.currentCaptureStatus);
    this.closeCropModal();
  }

  closeCropModal() {
    // If crop modal is open, close it and cancel selection
    if (this.cropModalElement) {
      document.body.removeChild(this.cropModalElement);
      this.cropModalElement = null;
      if (this.cropModalEscListener) {
        window.removeEventListener("keydown", this.cropModalEscListener);
        this.cropModalEscListener = null;
      }
      if (this.cropModalResolver) {
        this.cropModalResolver(null);
        this.cropModalResolver = null;
      }
    }
  }

  async captureScreen() {
    this.currentCaptureStatus = true;
    try {
      if (!this.stream) {
        this.stream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            displaySurface: "browser",
            //@ts-ignore
            selfBrowserSurface: "exclude",
            logicalSurface: true,
          } as any,
          //@ts-ignore
          preferCurrentTab: true,
          audio: false,
        });
        this.screenTrack = this.stream.getVideoTracks()[0];
        // Listen for manual stop (user stops sharing from browser UI)
        this.screenTrack.onended = () => {
          this.stream = null;
          this.screenTrack = null;
        };
      }
      if (this.screenTrack) {
        await this.trackToImage(this.screenTrack);
      } else {
        // fallback for legacy browsers
        const track = this.stream.getVideoTracks()[0];
        await this.trackToImage(track);
      }
    } catch (err) {
      this.currentCaptureStatus = false;
      console.error("Screen capture failed:", err);
    }
  }

  private async trackToImage(track: MediaStreamTrack): Promise<void> {
    const stream = new MediaStream([track]);
    const video = this.videoElement.nativeElement;
    const canvas = this.canvasElement.nativeElement;

    video.srcObject = stream;

    await new Promise<void>((resolve) => {
      video.onloadedmetadata = () => {
        video.play().then(resolve);
      };
    });

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      // Show modal overlay for region selection
      this.showCropModal(canvas).then((croppedDataUrl: string | null) => {
        if (croppedDataUrl) {
          this.urltoFile(
            croppedDataUrl,
            `sample${Math.random() * 10000}.png`,
            "image/png"
          )
            .then((e: any) => {
              this.addFileLabel(croppedDataUrl, e);
            })
            .catch((e) => {
              this.loggingService.error("Error capturing screenshot:", e);
            });
        }
      });
    } else {
      console.error("Failed to get 2D context from canvas");
    }
  }

  /**
   * Shows a modal overlay with the screenshot and allows the user to select a region to crop.
   * Returns a Promise that resolves to the cropped image data URL, or null if cancelled.
   */
  private showCropModal(
    sourceCanvas: HTMLCanvasElement
  ): Promise<string | null> {
    return new Promise((resolve) => {
      // Create modal overlay
      const modal = document.createElement("div");
      modal.style.position = "fixed";
      modal.style.top = "0";
      modal.style.left = "0";
      modal.style.width = "100vw";
      modal.style.height = "100vh";
      modal.style.background = "rgba(0,0,0,0.95)";
      modal.style.display = "flex";
      modal.style.alignItems = "center";
      modal.style.justifyContent = "center";
      modal.style.zIndex = "999";

      // Create preview canvas (full window)
      const previewCanvas = document.createElement("canvas");
      previewCanvas.width = sourceCanvas.width;
      previewCanvas.height = sourceCanvas.height;
      previewCanvas.style.border = "2px solid #fff";
      previewCanvas.style.cursor = "crosshair";
      previewCanvas.style.background = "#222";
      previewCanvas.getContext("2d")!.drawImage(sourceCanvas, 0, 0);
      previewCanvas.style.maxWidth = "100vw";
      previewCanvas.style.maxHeight = "100vh";
      previewCanvas.style.width = "100vw";
      previewCanvas.style.height = "100vh";

      modal.appendChild(previewCanvas);
      document.body.appendChild(modal);

      // Store modal and resolver for external closing
      this.cropModalElement = modal;
      this.cropModalResolver = resolve;

      // Selection logic
      let startX = 0,
        startY = 0,
        endX = 0,
        endY = 0,
        isDrawing = false;
      let rect: { x: number; y: number; w: number; h: number } | null = null;

      function drawRect() {
        const ctx = previewCanvas.getContext("2d")!;
        ctx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
        ctx.drawImage(sourceCanvas, 0, 0);
        if (rect) {
          ctx.save();
          ctx.strokeStyle = "#ff0000";
          ctx.lineWidth = 1;
          ctx.setLineDash([6]);
          ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
          ctx.restore();
        }
      }

      previewCanvas.onmousedown = (e) => {
        isDrawing = true;
        const rectB = previewCanvas.getBoundingClientRect();
        startX = ((e.clientX - rectB.left) * previewCanvas.width) / rectB.width;
        startY =
          ((e.clientY - rectB.top) * previewCanvas.height) / rectB.height;
        rect = null;
      };
      previewCanvas.onmousemove = (e) => {
        if (!isDrawing) return;
        const rectB = previewCanvas.getBoundingClientRect();
        endX = ((e.clientX - rectB.left) * previewCanvas.width) / rectB.width;
        endY = ((e.clientY - rectB.top) * previewCanvas.height) / rectB.height;
        rect = {
          x: Math.min(startX, endX),
          y: Math.min(startY, endY),
          w: Math.abs(endX - startX),
          h: Math.abs(endY - startY),
        };
        drawRect();
      };
      previewCanvas.onmouseup = (e) => {
        isDrawing = false;
        const rectB = previewCanvas.getBoundingClientRect();
        endX = ((e.clientX - rectB.left) * previewCanvas.width) / rectB.width;
        endY = ((e.clientY - rectB.top) * previewCanvas.height) / rectB.height;
        rect = {
          x: Math.min(startX, endX),
          y: Math.min(startY, endY),
          w: Math.abs(endX - startX),
          h: Math.abs(endY - startY),
        };
        drawRect();
        // On mouse up, if a valid rect, crop and resolve
        if (rect && rect.w > 0 && rect.h > 0) {
          const croppedCanvas = document.createElement("canvas");
          croppedCanvas.width = rect.w;
          croppedCanvas.height = rect.h;
          const croppedContext = croppedCanvas.getContext("2d")!;
          croppedContext.drawImage(
            sourceCanvas,
            rect.x,
            rect.y,
            rect.w,
            rect.h,
            0,
            0,
            rect.w,
            rect.h
          );
          const croppedDataUrl = croppedCanvas.toDataURL();
          document.body.removeChild(modal);
          // Clean up references
          this.cropModalElement = null;
          if (this.cropModalEscListener) {
            window.removeEventListener("keydown", this.cropModalEscListener);
            this.cropModalEscListener = null;
          }
          this.cropModalResolver = null;
          resolve(croppedDataUrl);
        }
      };

      // Also allow cancel with Escape key
      const escListener = (ev: KeyboardEvent) => {
        if (ev.key === "Escape") {
          document.body.removeChild(modal);
          window.removeEventListener("keydown", escListener);
          // Clean up references
          this.cropModalElement = null;
          this.cropModalEscListener = null;
          this.cropModalResolver = null;
          resolve(null);
        }
      };
      this.cropModalEscListener = escListener;
      window.addEventListener("keydown", escListener);
    });
  }

  adjustSelectionBox = (event: MouseEvent) => {
    const width = Math.abs(
      event.clientX - this.selectionBox.getBoundingClientRect().left
    );
    const height = Math.abs(
      event.clientY - this.selectionBox.getBoundingClientRect().top
    );

    console.log(
      "width",
      width,
      "height",
      height,
      "leftX",
      this.leftX,
      "topY",
      this.topY
    );

    this.renderer.setStyle(this.selectionBox, "left", this.leftX + "px");
    this.renderer.setStyle(this.selectionBox, "top", this.topY + "px");
    this.renderer.setStyle(this.selectionBox, "width", width + "px");
    this.renderer.setStyle(this.selectionBox, "height", height + "px");
  };

  finishSelection = () => {
    this.selectionBox.style.border = "none";
    this.renderer.removeChild(
      this.screenshotContainer.nativeElement,
      this.selectionBox
    );

    const html2canvasInstance: any = html2canvas;
    const max = 50;
    const extra = 55;
    if (
      this.selectionBox.offsetWidth > max &&
      this.selectionBox.offsetHeight > max
    ) {
      let yVal: any = null;
      if (this.selectionBox.getBoundingClientRect().top - extra < 0) {
        yVal = 0;
      } else {
        yVal = this.selectionBox.getBoundingClientRect().top - extra;
      }
      html2canvasInstance(this.screenshotContainer.nativeElement, {
        x: this.selectionBox.getBoundingClientRect().left,
        y: yVal,
        width: this.selectionBox.offsetWidth,
        height: this.selectionBox.offsetHeight,
        // allowTaint: true,
        // logging: true,
        // profile: true,
        // useCORS: true,
      }).then((canvas: HTMLCanvasElement) => {
        // let iframe = <HTMLIFrameElement>canvas.querySelector("iframe");
        // console.log("iframe", iframe);

        const screenshotUrl = canvas.toDataURL();
        this.urltoFile(
          screenshotUrl,
          `sample${Math.random() * 10000}.png`,
          "image/png"
        )
          .then((e: any) => {
            this.addFileLabel(screenshotUrl, e);
          })
          .catch((e) => {
            this.loggingService.error("Error capturing screenshot:", e);
          });
      });
    }
  };

  urltoFile(url: any, filename: any, mimeType: any) {
    if (url.startsWith("data:")) {
      const arr = url.split(",");
      const mime = arr[0].match(/^data:([^;]+);/)[1];
      const bstr = atob(arr[arr.length - 1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n) {
        n -= 1;
        u8arr[n] = bstr.charCodeAt(n);
      }
      const file = new File([u8arr], filename, { type: mime || mimeType });
      return Promise.resolve(file);
    }
    return fetch(url)
      .then((res) => res.arrayBuffer())
      .then((buf) => new File([buf], filename, { type: mimeType }));
  }

  addFileLabel(base64: any, file: any) {
    this.matDialog
      .open(AddFileModalComponent, {
        hasBackdrop: true,
        autoFocus: false,
        disableClose: true,
        width: "500px",
        maxHeight: "400px",
      })
      .afterClosed()
      .subscribe(async (val) => {
        if (val) {
          // Add check for duplicate labels
          const existingLabels = this.paradeImgArray.map(
            (img: any) => img.label
          );
          if (!existingLabels.includes(val)) {
            this.paradeImgArray.push({ base64, file, label: val });
            this.messageService.setParadeImage(this.paradeImgArray);
          } else {
            this.toastrService.error(
              "Label already exists. Please use a different label."
            );
          }
        }
        if (this.currentCaptureStatus) {
          this.captureScreen();
        }
      });
  }

  deleteItem(i: any) {
    this.paradeImgArray.splice(i, 1);
    this.messageService.setParadeImage(this.paradeImgArray);
  }

  clear() {
    this.paradeImgArray = [];
    this.closeCropModal();
    this.paradeImgArray = [];
    this.currentCaptureStatus = false;
    this.messageService.setCaptureStatus(false);
    this.messageService.setParadeImage(this.paradeImgArray);
    // Stop screen sharing if active
    if (this.screenTrack) {
      this.screenTrack.stop();
      this.screenTrack = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
  }

  navigateTo(url: any) {
    this.router.navigateByUrl(url);
  }

  openDetail() {
    this.paradeImgArray = [];
    this.currentCaptureStatus = !this.currentCaptureStatus;
    this.messageService.setCaptureStatus(this.currentCaptureStatus);
    this.router.navigateByUrl("parade/detail");
  }

  createParade() {
    // this.removeCapture();
    this.matDialog
      .open(ParadeCreateModalDialogComponent, {
        width: "500px",
        maxHeight: "400px",
        disableClose: true,
        autoFocus: false,
        data: { paradeData: this.paradeImgArray },
      })
      .afterClosed()
      .subscribe((id) => {
        if (id) {
          this.clear();
          this.router.navigateByUrl("parade/detail/" + btoa(id + ""));
        }
      });
  }

  mapParade() {
    this.matDialog
      .open(ParadeExistingModalDialogComponent, {
        width: "500px",
        maxHeight: "400px",
        disableClose: true,
        autoFocus: false,
        data: { paradeData: this.paradeImgArray },
      })
      .afterClosed()
      .subscribe((id) => {
        if (id) {
          this.clear();
          this.router.navigateByUrl("parade/detail/" + btoa(id + ""));
        }
      });
  }
}
